import {
  Calculator,
  Check,
  CircleAlert,
  Copy,
  Lock,
  Percent,
  Plus,
  Receipt,
  Tag,
  X,
  type LucideIcon,
} from "lucide-react"
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react"

import { useLocale } from "@/core/i18n/locale-context"
import { Button } from "@/ui/button"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"

import {
  CalcHeading,
  CalcIconChip,
  CalcShortcuts,
  Kbd,
  calcEnter,
  calcHeroInput,
  calcInputPanel,
  calcPanels,
  calcResultFigure,
  calcResultPanel,
  calcResultSlot,
  type ChipTone,
} from "./calc-ui"
import { formatAud } from "./calculator"
import {
  DEFAULT_GST_STATE,
  MAX_GST_LINES,
  QUICK_GST_AMOUNTS,
  calculateGstLines,
  calculateGstSingle,
  gstCopy,
  gstHeadline,
  gstHint,
  gstWorkings,
  parseGstSearch,
  stepGstAmount,
  toGstSearch,
  type GstMode,
  type GstStep,
} from "./gst-calculator-model"
import { GstSplitVisual } from "./gst-calculator-visual"

const MODE_VALUES: GstMode[] = ["add", "remove"]
const AMOUNT_KEYS = "ArrowUp ArrowDown Shift+ArrowUp Shift+ArrowDown Alt+ArrowUp Alt+ArrowDown"

/** ArrowUp/ArrowDown step on an amount field; any other key returns null. */
function arrowStep(e: KeyboardEvent<HTMLInputElement>): [1 | -1, GstStep] | null {
  if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return null
  return [e.key === "ArrowUp" ? 1 : -1, e.shiftKey ? "large" : e.altKey ? "small" : "normal"]
}

const DASH = "–"

/**
 * The interactive part of /gst-calculator (T915, launch pass T1040, pay-calculator look T1051). Runs in the
 * browser; the URL holds `?amount=&mode=` (no history entries). Input panel on the card surface, result panel on a
 * success tint, both the same height; every result state keeps the same rows so typing never moves the layout.
 * Keys are handled on the calculator root (never on document): arrows step the amount, "?" lists the shortcuts.
 */
export function GstCalculator() {
  const id = useId()
  const locale = useLocale()
  const c = gstCopy(locale)
  const [amount, setAmount] = useState(DEFAULT_GST_STATE.amount)
  const [mode, setMode] = useState<GstMode>(DEFAULT_GST_STATE.mode)
  const [multi, setMulti] = useState(false)
  const [lines, setLines] = useState<string[]>(["", ""])
  const [copied, setCopied] = useState<"ok" | "failed" | null>(null)
  const [keysOpen, setKeysOpen] = useState(false)
  // No motion on load or for the prerendered example: only after the user changes something.
  const [animate, setAnimate] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const keysButtonRef = useRef<HTMLButtonElement>(null)
  const keysId = `${id}-keys`

  // Restore a shared link after hydration, so the prerendered HTML always shows the default example.
  useEffect(() => {
    if (!window.location.search) return
    const state = parseGstSearch(window.location.search)
    // oxlint-disable-next-line react/set-state-in-effect -- reading the URL is the external system; doing it in render would mismatch the prerendered HTML
    setAmount(state.amount)
    setMode(state.mode)
  }, [])

  // Move focus into the shortcuts list when it opens, so screen readers announce it.
  useEffect(() => {
    if (keysOpen) document.getElementById(keysId)?.focus()
  }, [keysOpen, keysId])

  const update = (next: { amount?: string; mode?: GstMode }) => {
    const state = { amount, mode, ...next }
    if (next.amount !== undefined) setAmount(next.amount)
    if (next.mode !== undefined) setMode(next.mode)
    setCopied(null)
    setAnimate(true)
    window.history.replaceState(null, "", `${window.location.pathname}${toGstSearch(state)}`)
  }

  const setLine = (index: number, value: string) => {
    setAnimate(true)
    setLines((prev) => prev.map((v, i) => (i === index ? value : v)))
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied("ok")
    } catch {
      setCopied("failed")
    }
  }

  const closeKeys = (returnFocus: boolean) => {
    setKeysOpen(false)
    if (returnFocus) keysButtonRef.current?.focus()
  }

  const onRootKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      setKeysOpen(true)
    } else if (e.key === "Escape" && keysOpen) {
      e.preventDefault()
      closeKeys(true)
    }
  }

  const single = calculateGstSingle(amount, mode)
  const many = calculateGstLines(lines, mode)
  const amountLabel = mode === "remove" ? c.amountIncl : c.amountExcl
  const totals = multi
    ? many.kind === "ok"
      ? many.totals
      : null
    : single.kind === "ok"
      ? single
      : null
  const kind = multi ? many.kind : single.kind
  const badLine = multi && many.kind === "invalid" ? many.line : undefined
  const hintId = `${id}-hint`
  const hint = gstHint(kind, multi, badLine, locale)
  const headline = gstHeadline(totals, kind, mode, locale)

  const rows: [LucideIcon, ChipTone, string, string][] = [
    [Tag, "muted", c.rows.net, totals ? formatAud(totals.net) : DASH],
    [Percent, "primary", c.rows.gst, totals ? formatAud(totals.gst) : DASH],
    [Receipt, "success", c.rows.gross, totals ? formatAud(totals.gross) : DASH],
  ]
  const figure = totals ? formatAud(totals.gst) : DASH

  return (
    <div
      ref={rootRef}
      className="grid gap-4"
      onKeyDown={onRootKeyDown}
      onBlur={(e) => {
        if (keysOpen && !rootRef.current?.contains(e.relatedTarget)) closeKeys(false)
      }}
    >
      <div className={calcPanels}>
        <div className={calcInputPanel}>
          <div className="flex items-center justify-between gap-3">
            <CalcHeading icon={Calculator} tone="primary">
              {c.yourAmount}
            </CalcHeading>
            <CalcShortcuts
              ref={keysButtonRef}
              id={keysId}
              shortcuts={c.shortcuts}
              open={keysOpen}
              onToggle={() => setKeysOpen((open) => !open)}
            />
          </div>

          <div
            className={`border-input bg-background dark:bg-input/30 has-[[aria-invalid=true]]:border-destructive overflow-hidden rounded-xl border ${multi ? "" : "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]"}`}
          >
            <fieldset className="bg-muted m-0 border-0 p-1">
              <legend className="sr-only">{c.iWantTo}</legend>
              <div className="grid grid-cols-2 gap-1">
                {MODE_VALUES.map((value) => (
                  <label key={value} className="relative">
                    <input
                      type="radio"
                      name={`${id}-mode`}
                      value={value}
                      className="peer sr-only"
                      checked={mode === value}
                      onChange={() => update({ mode: value })}
                      aria-keyshortcuts="ArrowLeft ArrowRight"
                    />
                    <span className="text-muted-foreground peer-checked:bg-card peer-checked:text-primary peer-checked:ring-primary peer-focus-visible:ring-ring flex min-h-11 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-semibold peer-checked:shadow-sm peer-checked:ring-[1.5px] peer-focus-visible:ring-[3px]">
                      {mode === value && <Check className="size-4" aria-hidden="true" />}
                      {c.modes[value]}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {!multi ? (
              <div className="grid px-3.5 pt-2 pb-0.5">
                <Label htmlFor={`${id}-amount`} className="text-muted-foreground text-sm">
                  {amountLabel}
                </Label>
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground text-lg font-semibold" aria-hidden="true">
                    A$
                  </span>
                  <input
                    id={`${id}-amount`}
                    inputMode="decimal"
                    autoComplete="off"
                    className={calcHeroInput}
                    value={amount}
                    onChange={(e) => update({ amount: e.target.value })}
                    onKeyDown={(e) => {
                      const step = arrowStep(e)
                      if (!step) return
                      e.preventDefault()
                      update({ amount: stepGstAmount(amount, ...step) })
                    }}
                    aria-invalid={single.kind === "invalid"}
                    aria-describedby={hintId}
                    aria-keyshortcuts={AMOUNT_KEYS}
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-2 p-3">
                {lines.map((value, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Label htmlFor={`${id}-line-${index}`} className="sr-only">
                      {c.lineLabel(index + 1, mode === "remove")}
                    </Label>
                    <span className="text-muted-foreground w-6 text-right text-sm tabular-nums">
                      {index + 1}
                    </span>
                    <div className="relative grow">
                      <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
                        A$
                      </span>
                      <Input
                        id={`${id}-line-${index}`}
                        inputMode="decimal"
                        autoComplete="off"
                        className="bg-card h-11 pl-10 tabular-nums md:text-base"
                        value={value}
                        onChange={(e) => setLine(index, e.target.value)}
                        onKeyDown={(e) => {
                          const step = arrowStep(e)
                          if (!step) return
                          e.preventDefault()
                          setLine(index, stepGstAmount(value, ...step))
                        }}
                        aria-invalid={badLine === index}
                        aria-describedby={badLine === index ? hintId : undefined}
                        aria-keyshortcuts={AMOUNT_KEYS}
                      />
                    </div>
                    {lines.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-11"
                        aria-label={c.removeLine(index + 1)}
                        onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                      >
                        <X aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                ))}
                {lines.length < MAX_GST_LINES && (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 w-fit"
                    onClick={() => setLines((prev) => [...prev, ""])}
                  >
                    <Plus aria-hidden="true" />
                    {c.addLine}
                  </Button>
                )}
              </div>
            )}
          </div>

          <p
            id={hintId}
            className={`-mt-1.5 flex min-h-10 items-start gap-1.5 text-sm ${kind === "invalid" ? "text-destructive font-medium" : "text-muted-foreground"}`}
          >
            {kind === "invalid" && (
              <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            )}
            {hint}
          </p>

          {!multi && (
            <div role="group" aria-labelledby={`${id}-quick`} className="grid gap-1.5">
              <span id={`${id}-quick`} className="text-muted-foreground text-sm">
                {c.quickAmounts}
              </span>
              <div className="grid grid-cols-4 gap-2">
                {QUICK_GST_AMOUNTS.map((q) => (
                  <button
                    key={q.value}
                    type="button"
                    aria-pressed={amount === q.value}
                    onClick={() => update({ amount: q.value })}
                    className="border-border bg-background hover:bg-muted aria-pressed:border-primary aria-pressed:text-primary focus-visible:ring-ring min-h-11 rounded-md border px-1 text-sm font-medium tabular-nums outline-none focus-visible:ring-[3px]"
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="border-border text-muted-foreground flex min-h-11 cursor-pointer items-center gap-2.5 border-t pt-2.5 text-sm">
            <input
              type="checkbox"
              className="accent-primary size-4 shrink-0"
              checked={multi}
              onChange={(e) => {
                setAnimate(true)
                setMulti(e.target.checked)
              }}
            />
            {c.several(MAX_GST_LINES)}
          </label>

          <p className="text-muted-foreground mt-auto hidden flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] md:flex">
            <Kbd>↑</Kbd>
            <span>/</span>
            <Kbd>↓</Kbd>
            <span>{c.keysHint.change}</span>
            <span aria-hidden="true">·</span>
            <Kbd>Shift</Kbd>
            <span>{c.keysHint.ten}</span>
            <span aria-hidden="true">·</span>
            <Kbd>?</Kbd>
            <span>{c.keysHint.all}</span>
          </p>
        </div>

        <div data-testid="calc-result" aria-live="polite" className={calcResultPanel}>
          <CalcHeading icon={Receipt} tone="success">
            {c.result}
          </CalcHeading>
          <div className="grid gap-0.5">
            <span data-testid="calc-headline" className="text-muted-foreground text-sm">
              {headline}
            </span>
            <div className={calcResultSlot}>
              <span key={figure} className={`${calcResultFigure} ${calcEnter(animate)}`}>
                {figure}
              </span>
            </div>
            <span className="text-muted-foreground min-h-5 text-sm tabular-nums">
              {totals && (multi ? c.roundedPerLine : gstWorkings(totals, mode))}
            </span>
          </div>
          <table className="bg-card w-full rounded-md text-[15px]">
            <caption className="sr-only">{c.breakdownCaption}</caption>
            <tbody>
              {rows.map(([icon, tone, label, value], index) => {
                const strong = index === rows.length - 1
                return (
                  <tr key={label} className="border-border border-b last:border-b-0">
                    <th
                      scope="row"
                      className={`py-2.5 pr-3 pl-3 text-left ${strong ? "font-bold" : "font-normal"}`}
                    >
                      <span className="flex items-center gap-2.5">
                        <CalcIconChip icon={icon} tone={tone} />
                        {label}
                      </span>
                    </th>
                    <td
                      className={`py-2.5 pr-3 text-right tabular-nums ${strong ? "font-bold" : ""}`}
                    >
                      {value}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {/* ponytail: the link holds one amount and mode only, so it is offered for a single amount; add ?lines= if sharing several items is wanted */}
          <div className="flex min-h-11 flex-wrap items-center justify-between gap-x-3 gap-y-1">
            {!multi ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11"
                  onClick={copyLink}
                  disabled={single.kind !== "ok"}
                >
                  <Copy aria-hidden="true" />
                  {c.copyLink}
                </Button>
                <span className="text-muted-foreground text-sm">
                  {copied === "ok" && c.linkCopied}
                  {copied === "failed" && c.copyFailed}
                </span>
              </>
            ) : (
              <span className="text-muted-foreground text-sm">{c.shareSingle}</span>
            )}
          </div>
          <p className="text-muted-foreground flex items-center gap-1.5 text-sm">
            <Lock className="size-3.5 shrink-0" aria-hidden="true" />
            {c.privacy}
          </p>
          <p className="text-muted-foreground mt-auto text-[13px] leading-snug">{c.disclaimer}</p>
        </div>
      </div>

      <GstSplitVisual id={id} totals={totals} mode={mode} animate={animate} />
    </div>
  )
}
